/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useQuery } from '@tanstack/react-query';

// Lib
import { apiClient } from '@lib/api-client';

// Types
import {
	TApiResponseConversionFactorPopulated,
	TApiResponseGetConversionFactorsPopulated,
	TParsedApiResponseConversionFactor,
} from '../types';
import { TParams } from '@global-types/index';
import { TApiParams } from '@global-types/app/app';

// Utils
import {
	getRatioLabel,
	formatDate,
} from '@utils/functions';

// Constants
import { NOT_APPLICABLE } from '@config/constants/common';
import { queryKeys } from '@config/constants/query-keys';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */

export const getConversionFactors = (
	params: TApiParams
): Promise<TApiResponseGetConversionFactorsPopulated> => {
	return apiClient.get('/conversion_factors', {
		params: {
			page: params.page,
			pageSize: params.pageSize,
			populate: [
				'project',
				'site',
				'organization',
				'entity',
			],
			query: params.queryParams,
			sort: params.sortParams,
		},
	});
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useConversionFactorsApi = (
	page: number,
	size: number,
	queryParams: TParams,
	sortParams: string
) => {
	const { data, isFetching, isFetched } = useQuery({
		queryKey: [
			queryKeys.app.conversionFactors.list.key,
			{
				page: page,
				size: size,
				queryParams: queryParams,
				sortParams: sortParams,
			},
		],
		queryFn: () =>
			getConversionFactors({
				page: page,
				pageSize: size,
				queryParams: queryParams,
				sortParams: sortParams,
			}),
	});

	const updateList = (
		conversionFactors: TApiResponseConversionFactorPopulated[]
	) =>
		conversionFactors &&
		Array.isArray(conversionFactors) &&
		conversionFactors.reduce(
			(
				acc: TParsedApiResponseConversionFactor[],
				currentItem: TApiResponseConversionFactorPopulated
			) => {
				const ratio = getRatioLabel(
					currentItem.ratio,
					currentItem.unit
				);

				const modifiedItem: TParsedApiResponseConversionFactor =
					{
						...currentItem,
						site:
							currentItem?.site?.name || NOT_APPLICABLE,
						project:
							currentItem?.project?.name ||
							NOT_APPLICABLE,
						organization:
							currentItem?.organization?.name ||
							NOT_APPLICABLE,
						entity:
							currentItem?.entity?.name || NOT_APPLICABLE,
						startDate: formatDate(
							currentItem.startDate,
							'DD/MM/YYYY'
						),
						ratio: ratio,
						createdAt: formatDate(
							currentItem.createdAt,
							'DD/MM/YYYY'
						),
					};

				acc.push(modifiedItem);
				return acc;
			},
			[]
		);

	return {
		data:
			data && data.rows && Array.isArray(data.rows)
				? updateList(data.rows)
				: [],
		isLoading: isFetching && !isFetched,
		isFetched,
		total: data?.total,
		page: data?.page,
		pageSize: data?.pageSize,
		totalPages: data?.totalPages,
	};
};
